import React, { useState, useMemo } from "react";
import { Routes, Route, Link, useParams } from "react-router-dom";
import {
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  Badge,
  Button,
  Progress,
  Collapse
} from "reactstrap";
import SuiteLayout from "../../_suite/SuiteLayout";
import SimpleSettingsPage from "../../_suite/SimpleSettingsPage";
import DevopsDashboard from "../../devops";
import { Colxx, Separator } from "Components/CustomBootstrap";
import BreadcrumbContainer from "Components/BreadcrumbContainer";
import IntlMessages from "Util/IntlMessages";
import {
  SUITE_DEVOPS_BUDGET_ROW,
  SUITE_PROGRESS_HEIGHT_8,
  SUITE_PROGRESS_HEIGHT_10,
  DEVOPS_ERROR_BUDGET_BAR_COLUMN
} from "Constants/reactifyInlineStyles";
import { suiteApi } from "../../../../../api/suiteAdapters";
import { useAsyncResource } from "../../../../../hooks/useAsyncResource";
import { AsyncStateBoundary, SuitePageShell, KpiStatCards } from "../../_suite/primitives";

function ErrorBudgetHistogramBar({
  value,
  barIndex
}: {
  value: number;
  barIndex: number;
}) {
  const barStyle = useMemo(
    () => ({
      ...DEVOPS_ERROR_BUDGET_BAR_COLUMN,
      height: `${Math.max(8, value * 2)}px`,
      opacity: 0.35 + barIndex * 0.12
    }),
    [value, barIndex]
  );
  return (
    <div
      className="bg-primary rounded-top"
      style={barStyle}
      title={`${value}%`}
    />
  );
}

function ServicesSloPage() {
  const { data, loading, error } = useAsyncResource(
    () => suiteApi.devops.listServices(),
    []
  );
  return (
    <SuitePageShell heading={<IntlMessages id="suite.devops.services" />}>
      <AsyncStateBoundary loading={loading} error={error}>
        <Row>
          {(data || []).map(s => (
            <Col md="6" lg="4" key={s.id} className="mb-4">
              <Card
                className={`h-100 border-0 shadow-sm border-top border-4 ${
                  s.status === "up" ? "border-success" : "border-warning"
                }`}
              >
                <CardBody>
                  <h5>{s.name}</h5>
                  <p className="small text-muted">{s.region}</p>
                  <Badge color={s.status === "up" ? "success" : "warning"}>
                    {s.status}
                  </Badge>
                  <p className="mt-2 mb-1 small">
                    p99 {s.latencyMs}ms · SLO {s.sloLatencyMs ?? "—"}ms
                  </p>
                  {s.errorBudgetRemainingPct != null && (
                    <>
                      <div className="d-flex align-items-end gap-px mb-1" style={SUITE_DEVOPS_BUDGET_ROW}>
                        {(s.budgetSeries || [s.errorBudgetRemainingPct]).map(
                          (v, i) => (
                            <ErrorBudgetHistogramBar
                              key={i}
                              value={v}
                              barIndex={i}
                            />
                          )
                        )}
                      </div>
                      <div className="small text-muted mb-1">
                        Error budget remaining
                      </div>
                      <Progress
                        value={s.errorBudgetRemainingPct}
                        color={
                          s.errorBudgetRemainingPct < 8 ? "danger" : "success"
                        }
                        className="mb-2"
                        style={SUITE_PROGRESS_HEIGHT_8}
                      />
                    </>
                  )}
                  <Button
                    tag={Link}
                    color="link"
                    size="sm"
                    className="px-0"
                    to={encodeURIComponent(s.id)}
                    relative="path"
                  >
                    SLO detail →
                  </Button>
                </CardBody>
              </Card>
            </Col>
          ))}
        </Row>
      </AsyncStateBoundary>
    </SuitePageShell>
  );
}

function ServiceDetailPage() {
  const { id } = useParams();
  const { data, loading, error } = useAsyncResource(
    () => suiteApi.devops.getService(decodeURIComponent(id ?? "")),
    [id]
  );
  return (
    <div>
      <Row>
        <Colxx xxs="12">
          <BreadcrumbContainer
            heading={<IntlMessages id="suite.devops.service-detail" />}
          />
          <Separator className="mb-4" />
        </Colxx>
      </Row>
      <Button tag={Link} color="light" to=".." relative="path" className="mb-3">
        ← Back
      </Button>
      <AsyncStateBoundary loading={loading} error={error} empty={!data}>
        {data && (
          <>
            <KpiStatCards
              items={[
                { title: "Availability", value: "99.95%", sub: "30d" },
                {
                  title: "Error budget",
                  value: `${data.errorBudgetRemainingPct ?? "—"}%`,
                  sub: "remaining"
                },
                {
                  title: "Latency SLO",
                  value: `${data.latencyMs}ms`,
                  sub: `target ${data.sloLatencyMs ?? "—"}ms`
                }
              ]}
            />
            {data.errorBudgetRemainingPct != null && (
              <Progress
                value={data.errorBudgetRemainingPct}
                className="mb-4"
                style={SUITE_PROGRESS_HEIGHT_10}
                color={data.errorBudgetRemainingPct < 8 ? "danger" : "success"}
              />
            )}
          </>
        )}
      </AsyncStateBoundary>
    </div>
  );
}

function IncidentsTimelinePage() {
  const { data, loading, error } = useAsyncResource(
    () => suiteApi.devops.listIncidents(),
    []
  );
  return (
    <SuitePageShell heading={<IntlMessages id="suite.devops.incidents" />}>
      <AsyncStateBoundary loading={loading} error={error}>
        {(data || []).map(inc => (
          <Card key={inc.id} className="mb-3 border-0 shadow-sm">
            <CardBody className="d-flex justify-content-between flex-wrap">
              <div>
                <Badge color="danger" className="me-2">
                  {inc.severity}
                </Badge>
                <strong>{inc.id}</strong>
                <div>{inc.title}</div>
                <Badge color="light" className="text-dark">
                  {inc.state}
                </Badge>
              </div>
              <Button
                tag={Link}
                color="primary"
                size="sm"
                to={encodeURIComponent(inc.id)}
                relative="path"
              >
                Timeline
              </Button>
            </CardBody>
          </Card>
        ))}
      </AsyncStateBoundary>
    </SuitePageShell>
  );
}

function IncidentDetailPage() {
  const { id } = useParams();
  const { data, loading, error } = useAsyncResource(
    () => suiteApi.devops.getIncident(decodeURIComponent(id ?? "")),
    [id]
  );
  return (
    <div>
      <Row>
        <Colxx xxs="12">
          <BreadcrumbContainer
            heading={<IntlMessages id="suite.devops.incident-detail" />}
          />
          <Separator className="mb-4" />
        </Colxx>
      </Row>
      <Button tag={Link} color="light" to=".." relative="path" className="mb-3">
        ← Back
      </Button>
      <AsyncStateBoundary loading={loading} error={error} empty={!data}>
        {data && (
          <div className="ps-3 border-start border-danger">
            <div className="mb-3">
              <h4>{data.title}</h4>
              <Badge className="me-2">{data.severity}</Badge>
              <Badge color="light" className="text-dark">
                {data.state}
              </Badge>
              {data.startedAt && (
                <p className="small text-muted mb-0 mt-2">
                  Started {data.startedAt}
                </p>
              )}
            </div>
            {(data.timeline || []).map((ev, i) => (
              <Card key={i} className="mb-2">
                <CardBody className="py-2 small">
                  <strong>{ev.at}</strong> — {ev.label}
                </CardBody>
              </Card>
            ))}
            {(!data.timeline || data.timeline.length === 0) && (
              <p className="small text-muted">No timeline rows (wire PagerDuty).</p>
            )}
          </div>
        )}
      </AsyncStateBoundary>
    </div>
  );
}

function DeploysStreamPage() {
  const [openId, setOpenId] = useState<string | null>(null);
  const { data, loading, error } = useAsyncResource(
    () => suiteApi.devops.listDeploys(),
    []
  );
  return (
    <SuitePageShell
      heading={<IntlMessages id="suite.devops.deploys" />}
      afterBreadcrumb={
        <p className="text-muted small mb-0">
          <IntlMessages id="suite.devops.deploys-hint" />
        </p>
      }
    >
      <AsyncStateBoundary loading={loading} error={error}>
        {(data || []).map(d => (
          <div key={d.id} className="py-2 border-bottom">
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
              <div>
                <Badge className="me-2">{d.env}</Badge>
                <strong>{d.version}</strong>
                {d.diffSummary && (
                  <span className="small text-muted ms-2">{d.diffSummary}</span>
                )}
              </div>
              <div className="d-flex align-items-center gap-2">
                <Badge color={d.status === "healthy" ? "success" : "info"}>
                  {d.status}
                </Badge>
                <span className="small text-muted">{d.ago}</span>
                <Button
                  size="sm"
                  color="link"
                  className="p-0"
                  onClick={() => setOpenId(openId === d.id ? null : d.id)}
                >
                  {openId === d.id ? "Hide" : "Diff"}
                </Button>
              </div>
            </div>
            <Collapse isOpen={openId === d.id}>
              <Card className="mt-2 bg-light border-0">
                <CardBody className="py-2 small">
                  <p className="mb-1 font-weight-bold">
                    <IntlMessages id="suite.devops.diff-placeholder" />
                  </p>
                  <ul className="mb-0 ps-3">
                    {(d.commits || []).map((c, i) => (
                      <li key={i}>{c}</li>
                    ))}
                  </ul>
                </CardBody>
              </Card>
            </Collapse>
          </div>
        ))}
      </AsyncStateBoundary>
    </SuitePageShell>
  );
}

function RunbooksLibraryPage() {
  const { data, loading, error } = useAsyncResource(
    () => suiteApi.devops.listRunbooks(),
    []
  );
  return (
    <SuitePageShell heading={<IntlMessages id="suite.devops.runbooks" />}>
      <AsyncStateBoundary loading={loading} error={error}>
        <Row>
          {(data || []).map(r => (
            <Col md="6" key={r.id} className="mb-3">
              <Card className="h-100 border-0 shadow-sm bg-light">
                <CardBody>
                  <CardTitle tag="h5">{r.title}</CardTitle>
                  <p className="small text-muted">Owner: {r.owner}</p>
                  <p className="small mb-1">Updated {r.updated}</p>
                  {r.steps != null && (
                    <p className="small text-muted mb-2">{r.steps} steps</p>
                  )}
                  <Button color="primary" size="sm" className="mt-2">
                    Open runbook
                  </Button>
                </CardBody>
              </Card>
            </Col>
          ))}
        </Row>
      </AsyncStateBoundary>
    </SuitePageShell>
  );
}

function OncallCalendarStripPage() {
  const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  return (
    <div>
      <div className="d-flex gap-2 flex-wrap mb-4">
        {days.map(d => (
          <Card key={d} className="text-center px-3 py-2 border-primary">
            <div className="small text-muted">{d}</div>
            <strong>P</strong>
          </Card>
        ))}
      </div>
      <SimpleSettingsPage
        breadcrumbHeadingId="suite.devops.oncall"
        titleId="suite.devops.oncall"
        fetchSettings={suiteApi.devops.getOncall}
        onSave={suiteApi.devops.saveOncall}
        fields={[
          { name: "primary", labelId: "devops.oncall.primary" },
          { name: "secondary", labelId: "devops.oncall.secondary" },
          { name: "rotation", labelId: "suite.col.rotation" }
        ]}
      />
    </div>
  );
}

export function DevopsSuiteSection() {
  return (
    <Routes>
      <Route element={<SuiteLayout vertical="devops" />}>
        <Route index element={<DevopsDashboard />} />
        <Route path="services" element={<ServicesSloPage />} />
        <Route path="services/:id" element={<ServiceDetailPage />} />
        <Route path="incidents" element={<IncidentsTimelinePage />} />
        <Route path="incidents/:id" element={<IncidentDetailPage />} />
        <Route path="deploys" element={<DeploysStreamPage />} />
        <Route path="runbooks" element={<RunbooksLibraryPage />} />
        <Route path="oncall" element={<OncallCalendarStripPage />} />
      </Route>
    </Routes>
  );
}
